import * as React from "react";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * 아이콘 전용 버튼.
 * - `forwardRef`: Radix Tooltip `TooltipTrigger asChild`가 트리거 DOM에 ref를 붙이므로 필요합니다.
 *   (상위에서 ref를 넘기지 않아도 Tooltip 내부에서 ref 병합에 사용됩니다.)
 */
export type BtnIconProps = React.ComponentPropsWithoutRef<"button"> & {
  /** 버튼 안에 그릴 아이콘. 지정 시 `children`보다 우선합니다. */
  icon?: React.ReactNode;
  /** 넣으면 버튼을 Tooltip으로 감쌉니다. */
  tooltip?: React.ReactNode;
  /** TooltipContent `side` */
  tooltipSide?: React.ComponentPropsWithoutRef<typeof TooltipContent>["side"];
  /** TooltipContent `sideOffset` */
  tooltipSideOffset?: React.ComponentPropsWithoutRef<
    typeof TooltipContent
  >["sideOffset"];
  /** 버튼 크기 */
  size?: React.ComponentPropsWithoutRef<typeof Button>["size"];
};

export const BtnIcon = React.forwardRef<HTMLButtonElement, BtnIconProps>(
  function BtnIcon(
    {
      className,
      type = "button",
      tooltip,
      tooltipSide = "top",
      tooltipSideOffset,
      icon,
      children,
      size,
      ...rest
    },
    ref
  ) {
    const content = icon ?? children;

    const buttonEl = (
      <Button
        ref={ref}
        type={type}
        className={cn(
          buttonVariants({ variant: "outline", size: size ?? "default" }),
          "rounded-full bg-transparent! hover:border-foreground/40 text-foreground/50 hover:text-foreground",

          className
        )}
        {...rest}
      >
        {content}
      </Button>
    );

    if (tooltip != null && tooltip !== "") {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
          <TooltipContent side={tooltipSide} sideOffset={tooltipSideOffset}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      );
    }

    return buttonEl;
  }
);

BtnIcon.displayName = "BtnIcon";
